import { brandStorage as localStorage } from '@/utils/brandSession';
/**
 * Dashboard Criticità Immobili
 * Analizza ticket per identificare problemi ricorrenti sugli immobili
 * Visibile a: Admin, Bookings Operator, Customer Service
 */
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  AlertTriangle, 
  Building, 
  TrendingUp, 
  TrendingDown,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Thermometer,
  Wifi,
  Sofa,
  Package,
  Wrench,
  Clock,
  Sparkles,
  UserX,
  CreditCard,
  MessageSquare,
  AlertCircle,
  HelpCircle,
  BarChart3,
  PieChart,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/config';

const CATEGORY_ICONS = {
  clima: Thermometer,
  impianti: Wifi,
  comfort: Sofa,
  dotazioni: Package,
  manutenzione: Wrench,
  checkin: Clock,
  pulizia: Sparkles,
  ospite: UserX,
  pagamenti: CreditCard,
  comunicazioni: MessageSquare,
  sistema: AlertCircle,
  altro: HelpCircle,
  non_classificato: HelpCircle
};

// Colori per gruppi
const GROUP_COLORS = {
  immobile: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100' },
  responsabile: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100' },
  holidayworld: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100' },
  altro: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', badge: 'bg-gray-100' }
};

const CATEGORY_INFO = {
  clima: { label: "Clima/Riscaldamento", group: "immobile" },
  impianti: { label: "Impianti", group: "immobile" },
  comfort: { label: "Comfort", group: "immobile" },
  dotazioni: { label: "Dotazioni", group: "immobile" },
  manutenzione: { label: "Manutenzione", group: "immobile" },
  checkin: { label: "Check-in/out", group: "responsabile" },
  pulizia: { label: "Pulizia", group: "responsabile" },
  ospite: { label: "Ospite", group: "responsabile" },
  pagamenti: { label: "Pagamenti", group: "holidayworld" },
  comunicazioni: { label: "Comunicazioni", group: "holidayworld" },
  sistema: { label: "Sistema", group: "holidayworld" },
  altro: { label: "Altro", group: "altro" },
  non_classificato: { label: "Non classificato", group: "altro" }
};

const IssuesDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [trends, setTrends] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [expandedProperty, setExpandedProperty] = useState(null);
  const [daysFilter, setDaysFilter] = useState(90);
  const [classifying, setClassifying] = useState(false);
  
  const token = localStorage.getItem('hw_token');

  useEffect(() => {
    fetchData();
  }, [daysFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch analysis
      const analysisRes = await fetch(`${API}/api/issues/analysis?days=${daysFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (analysisRes.ok) {
        setAnalysis(await analysisRes.json());
      }

      // Fetch trends
      const trendsRes = await fetch(`${API}/api/issues/trends?months=6`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (trendsRes.ok) {
        setTrends(await trendsRes.json());
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const classifyAllTickets = async () => {
    setClassifying(true);
    try {
      const res = await fetch(`${API}/api/issues/classify-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Classificati ${data.classified} ticket`);
        fetchData();
      }
    } catch (error) {
      toast.error('Errore nella classificazione');
    } finally {
      setClassifying(false);
    }
  };

  const CategoryBadge = ({ category }) => {
    const info = CATEGORY_INFO[category] || CATEGORY_INFO.altro;
    const colors = GROUP_COLORS[info.group] || GROUP_COLORS.altro;
    const Icon = CATEGORY_ICONS[category] || HelpCircle;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${colors.badge} ${colors.text}`}>
        <Icon className="w-3 h-3" />
        {info.label}
      </span>
    );
  };

  const PropertyCard = ({ property }) => {
    const isExpanded = expandedProperty === property.property_id;
    const criticalityLevel = property.criticality_score > 20 ? 'critical' : 
                            property.criticality_score > 10 ? 'warning' : 'normal';
    
    const levelColors = {
      critical: 'border-l-red-500 bg-red-50/50',
      warning: 'border-l-yellow-500 bg-yellow-50/50',
      normal: 'border-l-green-500 bg-green-50/50'
    };

    return (
      <Card className={`border-l-4 ${levelColors[criticalityLevel]} overflow-hidden`}>
        <div 
          className="p-4 cursor-pointer hover:bg-slate-50/50 transition"
          onClick={() => setExpandedProperty(isExpanded ? null : property.property_id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${criticalityLevel === 'critical' ? 'bg-red-100' : criticalityLevel === 'warning' ? 'bg-yellow-100' : 'bg-green-100'}`}>
                <Building className={`w-5 h-5 ${criticalityLevel === 'critical' ? 'text-red-600' : criticalityLevel === 'warning' ? 'text-yellow-600' : 'text-green-600'}`} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800 notranslate" translate="no">
                  {property.property_name}
                </h3>
                <p className="text-sm text-slate-500">
                  {property.property_code} • {property.city}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="flex items-center gap-2">
                  <span className={`text-2xl font-bold ${criticalityLevel === 'critical' ? 'text-red-600' : criticalityLevel === 'warning' ? 'text-yellow-600' : 'text-green-600'}`}>
                    {property.total_tickets}
                  </span>
                  <span className="text-sm text-slate-500">ticket</span>
                </div>
                <div className="flex gap-2 text-xs">
                  {property.open_tickets > 0 && (
                    <span className="text-red-600">{property.open_tickets} aperti</span>
                  )}
                  {property.urgent_tickets > 0 && (
                    <span className="text-orange-600">{property.urgent_tickets} urgenti</span>
                  )}
                </div>
              </div>
              {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </div>
          </div>
          
          {/* Categorie breakdown (sempre visibile) */}
          {Object.keys(property.category_breakdown).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(property.category_breakdown)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([cat, count]) => (
                  <div key={cat} className="flex items-center gap-1">
                    <CategoryBadge category={cat} />
                    <span className="text-xs font-medium text-slate-600">×{count}</span>
                  </div>
                ))}
            </div>
          )}
          
          {/* Problemi ricorrenti alert */}
          {Object.keys(property.recurring_issues).length > 0 && (
            <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-orange-800">
                <AlertTriangle className="w-4 h-4" />
                <span className="font-medium">Problemi ricorrenti:</span>
                {Object.entries(property.recurring_issues).map(([cat, count]) => (
                  <span key={cat}>{CATEGORY_INFO[cat]?.label || cat} ({count}×)</span>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Dettagli espansi */}
        {isExpanded && (
          <div className="border-t bg-white p-4">
            <h4 className="font-medium text-slate-700 mb-3">Ultimi ticket</h4>
            <div className="space-y-2">
              {property.issues.slice(0, 5).map((issue) => (
                <div key={issue.ticket_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CategoryBadge category={issue.category} />
                    <span className="text-sm text-slate-700">{issue.subject}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${issue.status === 'aperto' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                      {issue.status}
                    </span>
                    <span className="text-xs text-slate-500">
                      {new Date(issue.created_at).toLocaleDateString('it-IT')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            <Button 
              variant="outline" 
              className="mt-3 w-full"
              onClick={() => window.open(`/admin/tickets?property=${property.property_id}`, '_blank')}
            >
              Vedi tutti i ticket <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <AlertTriangle className="w-7 h-7 text-orange-500" />
                Dashboard Criticità Immobili
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                Analisi problemi ricorrenti basata sui ticket
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Filtro periodo */}
              <select
                value={daysFilter}
                onChange={(e) => setDaysFilter(Number(e.target.value))}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value={30}>Ultimi 30 giorni</option>
                <option value={90}>Ultimi 90 giorni</option>
                <option value={180}>Ultimi 6 mesi</option>
                <option value={365}>Ultimo anno</option>
              </select>
              
              <Button 
                variant="outline" 
                onClick={classifyAllTickets}
                disabled={classifying}
              >
                {classifying ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <BarChart3 className="w-4 h-4 mr-2" />
                )}
                Classifica con AI
              </Button>
              
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Aggiorna
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats Cards */}
        {analysis?.global_stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4 border-l-4 border-l-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Immobili con segnalazioni</p>
                  <p className="text-3xl font-bold text-slate-800">{analysis.global_stats.total_properties_with_issues}</p>
                </div>
                <Building className="w-10 h-10 text-blue-500 opacity-50" />
              </div>
            </Card>
            
            <Card className="p-4 border-l-4 border-l-slate-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Ticket totali</p>
                  <p className="text-3xl font-bold text-slate-800">{analysis.global_stats.total_tickets}</p>
                </div>
                <MessageSquare className="w-10 h-10 text-slate-500 opacity-50" />
              </div>
            </Card>
            
            <Card className="p-4 border-l-4 border-l-red-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Ticket aperti</p>
                  <p className="text-3xl font-bold text-red-600">{analysis.global_stats.total_open}</p>
                </div>
                <AlertCircle className="w-10 h-10 text-red-500 opacity-50" />
              </div>
            </Card>
            
            <Card className="p-4 border-l-4 border-l-orange-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Urgenti</p>
                  <p className="text-3xl font-bold text-orange-600">{analysis.global_stats.total_urgent}</p>
                </div>
                <AlertTriangle className="w-10 h-10 text-orange-500 opacity-50" />
              </div>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista immobili */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-800">
                Immobili per criticità
              </h2>
              <span className="text-sm text-slate-500">
                Ordinati per numero segnalazioni
              </span>
            </div>
            
            {analysis?.properties?.length > 0 ? (
              <div className="space-y-3">
                {analysis.properties.map((property) => (
                  <PropertyCard key={property.property_id} property={property} />
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <Building className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Nessuna segnalazione nel periodo selezionato</p>
              </Card>
            )}
          </div>

          {/* Sidebar con categorie e trend */}
          <div className="space-y-6">
            {/* Top categorie */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-blue-500" />
                  Top Categorie
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analysis?.global_stats?.top_categories && 
                    Object.entries(analysis.global_stats.top_categories)
                      .slice(0, 8)
                      .map(([cat, count]) => {
                        const info = CATEGORY_INFO[cat] || {};
                        const colors = GROUP_COLORS[info.group] || GROUP_COLORS.altro;
                        const percentage = Math.round((count / analysis.global_stats.total_tickets) * 100);
                        
                        return (
                          <div key={cat} className="flex items-center gap-2">
                            <CategoryBadge category={cat} />
                            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${colors.badge}`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-slate-600 w-12 text-right">
                              {count}
                            </span>
                          </div>
                        );
                      })
                  }
                </div>
              </CardContent>
            </Card>

            {/* Legenda gruppi */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Legenda Categorie</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-sm text-slate-600">Problemi Immobile</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm text-slate-600">Problemi Gestione</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  <span className="text-sm text-slate-600">Holiday World</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                  <span className="text-sm text-slate-600">Altro / Non classificato</span>
                </div>
              </CardContent>
            </Card>

            {/* Trend mensile */}
            {trends?.trends && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    Trend Mensile
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {trends.trends.map((month, idx) => {
                      const prevMonth = trends.trends[idx - 1];
                      const trend = prevMonth ? month.total - prevMonth.total : 0;
                      
                      return (
                        <div key={month.month} className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">{month.month}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{month.total}</span>
                            {trend !== 0 && (
                              <span className={`text-xs flex items-center ${trend > 0 ? 'text-red-500' : 'text-green-500'}`}>
                                {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                {Math.abs(trend)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IssuesDashboard;
